import React, { useMemo } from 'react'
import { Typography } from './Typography'

// ============================================================================
// Icons
// ============================================================================

const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M15 18L9 12L15 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M9 18L15 12L9 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const DoubleChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M11 18L5 12L11 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M18 18L12 12L18 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const DoubleChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <path
      d='M13 18L19 12L13 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M6 18L12 12L6 6'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// ============================================================================
// Date helpers (kept self-contained so MonthCalendar can be reused without
// pulling in the rest of the date-range machinery).
// ============================================================================

export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const WEEKDAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export function isSameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  if (!a || !b) return false
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function isInRangeExclusive(
  date: Date,
  start: Date | null | undefined,
  end: Date | null | undefined
): boolean {
  if (!start || !end) return false
  const d = date.getTime()
  return d > start.getTime() && d < end.getTime()
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date())
}

export function isSameMonth(date: Date, year: number, month: number): boolean {
  return date.getFullYear() === year && date.getMonth() === month
}

function toApiDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDayLabel(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}

/** Inclusive YYYY-MM-DD bounds check; either bound may be omitted. */
export function isDateInSelectableRange(
  date: Date,
  minYmd: string | undefined,
  maxYmd: string | undefined
): boolean {
  const ymd = toApiDateString(date)
  if (minYmd && ymd < minYmd) return false
  if (maxYmd && ymd > maxYmd) return false
  return true
}

/**
 * 6×7 grid, Sunday-first columns. Leading and trailing cells are real dates in
 * adjacent months (rendered muted by the consumer).
 */
export function getMonthGrid(year: number, month: number): Date[][] {
  const firstDayOfWeek = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const grid: Date[][] = []
  let week: Date[] = []

  const prevMonthDays = new Date(year, month, 0).getDate()
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    week.push(new Date(year, month - 1, prevMonthDays - i))
  }

  for (let day = 1; day <= daysInMonth; day++) {
    week.push(new Date(year, month, day))
    if (week.length === 7) {
      grid.push(week)
      week = []
    }
  }

  if (week.length > 0) {
    let nextDay = 1
    while (week.length < 7) {
      week.push(new Date(year, month + 1, nextDay++))
    }
    grid.push(week)
  }

  while (grid.length < 6) {
    const lastRow = grid[grid.length - 1]
    const lastDate = lastRow[6]
    const nextWeek: Date[] = []
    for (let i = 1; i <= 7; i++) {
      nextWeek.push(new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate() + i))
    }
    grid.push(nextWeek)
  }

  return grid
}

// ============================================================================
// Component
// ============================================================================

export interface MonthCalendarProps {
  /** Year of the displayed month */
  year: number
  /** Month index (0-11) of the displayed month */
  month: number

  /**
   * Single mode: pass `selectedStart` only.
   * Range mode: pass both `selectedStart` and `selectedEnd`.
   */
  selectedStart?: Date | null
  selectedEnd?: Date | null

  /**
   * Optional preview range for hover affordances. When provided the in-range
   * fill follows `previewStart`/`previewEnd` instead of `selectedStart`/`selectedEnd`.
   */
  previewStart?: Date | null
  previewEnd?: Date | null

  /** Inclusive YYYY-MM-DD bounds for selectable days. */
  minSelectableDate?: string
  maxSelectableDate?: string

  /** Day click handler */
  onDateClick: (date: Date) => void
  /** Optional hover/leave hooks for range preview */
  onDateHover?: (date: Date) => void
  onDateLeave?: () => void

  /**
   * Header navigation. Each arrow only renders when the matching handler is
   * provided, so consumers can opt into year-stepping (`<<`, `>>`) or
   * month-stepping (`<`, `>`) independently.
   */
  onPrevMonth?: () => void
  onNextMonth?: () => void
  onPrevYear?: () => void
  onNextYear?: () => void

  /** Click on the "May 2026" title — typically opens a month/year picker. */
  onMonthYearClick?: () => void

  /** Use the smaller cell sizes used in compact viewports. */
  compact?: boolean

  /** Optional test id forwarded onto the panel root. */
  panelTestId?: string
}

/**
 * Single-month calendar grid with a configurable navigation header.
 *
 * Used in two places today:
 *  - {@link DateRangePanel}: stacks two of these to form the billing-period
 *    range picker (the "two calendars hooked together").
 *  - {@link DatePickerPopover}: renders one for single-date selection.
 */
export const MonthCalendar: React.FC<MonthCalendarProps> = ({
  year,
  month,
  selectedStart,
  selectedEnd,
  previewStart,
  previewEnd,
  minSelectableDate,
  maxSelectableDate,
  onDateClick,
  onDateHover,
  onDateLeave,
  onPrevMonth,
  onNextMonth,
  onPrevYear,
  onNextYear,
  onMonthYearClick,
  compact = false,
  panelTestId
}) => {
  const grid = useMemo(() => getMonthGrid(year, month), [year, month])

  const renderNavButton = (
    handler: (() => void) | undefined,
    Icon: React.FC<{ className?: string }>,
    ariaLabel: string
  ) => {
    if (!handler) return null
    return (
      <button
        type='button'
        aria-label={ariaLabel}
        onClick={handler}
        className='size-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-4 rounded cursor-pointer transition-colors'
      >
        <Icon className='size-4 text-tokens-neutral-text-quaternary' />
      </button>
    )
  }

  return (
    <div data-testid={panelTestId} className={`flex flex-col ${compact ? 'w-full' : 'w-[280px]'}`}>
      {/* Month header — order matters: prevYear, prevMonth, title, nextMonth, nextYear */}
      <div className='flex items-center justify-between px-2 py-[9px] border-b border-tokens-neutral-fill-fill-6'>
        <div className='flex items-center'>
          {renderNavButton(onPrevYear, DoubleChevronLeftIcon, 'Previous year')}
          {renderNavButton(onPrevMonth, ChevronLeftIcon, 'Previous month')}
        </div>

        {onMonthYearClick ? (
          <button
            type='button'
            onClick={onMonthYearClick}
            className='hover:bg-tokens-neutral-fill-fill-4 px-2 py-0.5 rounded cursor-pointer transition-colors'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-primary'
              style={{ fontSize: '14px', lineHeight: '22px' }}
            >
              {MONTHS_SHORT[month]} {year}
            </Typography>
          </button>
        ) : (
          <div className='px-2 py-0.5'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='semibold'
              className='text-tokens-neutral-text-primary'
              style={{ fontSize: '14px', lineHeight: '22px' }}
            >
              {MONTHS_SHORT[month]} {year}
            </Typography>
          </div>
        )}

        <div className='flex items-center'>
          {renderNavButton(onNextMonth, ChevronRightIcon, 'Next month')}
          {renderNavButton(onNextYear, DoubleChevronRightIcon, 'Next year')}
        </div>
      </div>

      {/* Calendar body */}
      <div className={`${compact ? 'px-2' : 'px-[18px]'} py-2 flex flex-col gap-1`}>
        <div className='grid grid-cols-7'>
          {WEEKDAYS_SHORT.map(day => (
            <div key={day} className='h-[24px] flex items-center justify-center'>
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                fontWeight='normal'
                className='text-tokens-neutral-text-tertiary'
                style={{ fontSize: '12px', lineHeight: '20px' }}
              >
                {day}
              </Typography>
            </div>
          ))}
        </div>

        {grid.map((week, weekIdx) => (
          <div key={weekIdx} className='grid grid-cols-7'>
            {week.map((date, dayIdx) => {
              const isCurrentMonth = isSameMonth(date, year, month)
              /**
               * Selection styling only applies to cells inside this panel's month —
               * avoids duplicate rings on spillover copies of the same day in the
               * adjacent calendar (DateRangePanel renders two months side by side).
               */
              const inPanelMonth = isCurrentMonth
              const canPick =
                inPanelMonth && isDateInSelectableRange(date, minSelectableDate, maxSelectableDate)

              const isStart =
                inPanelMonth &&
                (isSameDay(date, selectedStart) ||
                  (previewStart != null && isSameDay(date, previewStart)))
              const isEnd =
                inPanelMonth &&
                (isSameDay(date, selectedEnd) ||
                  (previewEnd != null && isSameDay(date, previewEnd)))
              const inRange = inPanelMonth && isInRangeExclusive(date, previewStart, previewEnd)
              const today = isToday(date)

              return (
                <button
                  type='button'
                  key={dayIdx}
                  disabled={!canPick}
                  aria-label={formatDayLabel(date)}
                  aria-pressed={isStart || isEnd}
                  className={`
                    ${compact ? 'h-[32px]' : 'h-[34px]'} flex items-center justify-center relative border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-1
                    ${canPick ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}
                    ${inRange ? 'bg-[rgba(132,111,225,0.08)]' : ''}
                    ${isStart ? 'rounded-l-full' : ''}
                    ${isEnd ? 'rounded-r-full' : ''}
                  `}
                  onClick={() => onDateClick(date)}
                  onMouseEnter={() => {
                    if (canPick && onDateHover) onDateHover(date)
                  }}
                  onMouseLeave={() => {
                    if (onDateLeave) onDateLeave()
                  }}
                >
                  <div
                    className={`
                      ${compact ? 'size-[26px]' : 'size-[28px]'} flex items-center justify-center rounded-full text-[14px] transition-colors
                      ${
                        isStart || isEnd
                          ? 'bg-tokens-status-link-1 text-white font-medium'
                          : today && isCurrentMonth
                            ? 'border border-tokens-status-link-1 text-tokens-status-link-1 font-medium'
                            : isCurrentMonth
                              ? 'text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6'
                              : 'text-tokens-neutral-text-quaternary hover:bg-tokens-neutral-fill-fill-4'
                      }
                    `}
                  >
                    {date.getDate()}
                  </div>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

export default MonthCalendar
